import { useEffect } from 'react';
import Navbar from '@/pages/home/components/Navbar';
import FooterSection from '@/pages/home/components/FooterSection';
import Breadcrumbs from '@/pages/Blog/components/Breadcrumbs';
import JsonLd, { breadcrumbSchema } from '@/components/base/JsonLd';
import useSeo, { SITE_URL, OG_IMAGE } from '@/hooks/useSeo';

const sections = [
  {
    title: '1. Acceptance of Terms',
    content: 'By downloading, accessing, or using the TNT Coach AI mobile application and related services (collectively, the "Service"), you agree to be bound by these Terms of Service. If you do not agree to all the terms and conditions, you may not use the Service.',
  },
  {
    title: '2. Description of Service',
    content: 'TNT Coach AI is a mobile coaching application that provides AI-generated coaching responses, mindset reframes, and action guidance based on user input. The Service is intended for personal development and self-improvement purposes. It is not a substitute for professional therapy, counseling, medical advice, or clinical mental health treatment.',
  },
  {
    title: '3. User Responsibilities',
    content: 'You are solely responsible for the content you submit to the Service and for how you interpret and act upon the coaching responses provided. You agree not to use the Service for any unlawful purpose or in violation of any applicable laws or regulations. You must be at least 13 years of age to use the Service. Users under 18 must have parental or guardian consent.',
  },
  {
    title: '4. AI-Generated Content',
    content: 'The coaching responses provided by TNT Coach AI are generated by artificial intelligence. While we strive for high-quality output, we do not guarantee the accuracy, completeness, or appropriateness of any AI-generated content. Coaching responses should be treated as suggestions and perspectives, not as professional advice. You should use your own judgment before acting on any guidance received through the Service.',
  },
  {
    title: '5. Subscriptions and Payments',
    content: 'TNT Coach AI offers both free and paid subscription tiers. Paid subscriptions are billed in advance on a recurring basis as selected by you (monthly or annually). All fees are non-refundable except as required by applicable law. You may cancel your subscription at any time through your app store account settings. Cancellation will take effect at the end of the current billing period. We reserve the right to change our pricing upon reasonable notice.',
  },
  {
    title: '6. Intellectual Property',
    content: 'The TNT Coach AI name, logo, branding, app design, and all original content provided through the Service are the exclusive property of TNT Coach AI and its licensors. You may not copy, modify, distribute, or create derivative works based on the Service without our prior written consent. Any feedback, suggestions, or ideas you provide regarding the Service may be used by us without restriction or compensation.',
  },
  {
    title: '7. Disclaimer of Warranties',
    content: 'The Service is provided on an "as is" and "as available" basis, without warranties of any kind, either express or implied. TNT Coach AI does not warrant that the Service will be uninterrupted, error-free, secure, or free from viruses or other harmful components. We expressly disclaim any warranties of merchantability, fitness for a particular purpose, and non-infringement to the fullest extent permitted by law.',
  },
  {
    title: '8. Limitation of Liability',
    content: 'To the maximum extent permitted by applicable law, TNT Coach AI and its officers, directors, employees, and agents shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of or relating to your use of the Service. This includes, but is not limited to, damages for loss of profits, data, goodwill, or other intangible losses. Our total liability for any claims relating to the Service shall not exceed the amount you paid us in the twelve months preceding the claim.',
  },
  {
    title: '9. Indemnification',
    content: 'You agree to indemnify and hold harmless TNT Coach AI and its affiliates from and against any claims, damages, liabilities, costs, and expenses (including reasonable legal fees) arising out of your use of the Service, your violation of these Terms, or your infringement of any third-party rights.',
  },
  {
    title: '10. Termination',
    content: 'We reserve the right to suspend or terminate your access to the Service at any time, with or without cause, and with or without notice. Upon termination, your right to use the Service will immediately cease. All provisions of these Terms that by their nature should survive termination shall survive, including ownership provisions, warranty disclaimers, indemnity, and limitations of liability.',
  },
  {
    title: '11. Changes to Terms',
    content: 'We may modify these Terms of Service at any time. We will notify you of material changes by posting the updated Terms within the app and updating the "Last Updated" date. Your continued use of the Service after any changes constitutes your acceptance of the new Terms. We encourage you to review these Terms periodically.',
  },
  {
    title: '12. Governing Law',
    content: 'These Terms shall be governed by and construed in accordance with the laws of the jurisdiction in which TNT Coach AI operates, without regard to conflict of law principles. Any disputes arising under these Terms shall be resolved exclusively in the courts of that jurisdiction.',
  },
  {
    title: '13. Contact',
    content: 'If you have any questions about these Terms of Service, please contact us at support@tntcoach.ai.',
  },
];

export default function TermsPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useSeo({
    title: 'Terms of Service | TNT Coach AI',
    description: 'Review the TNT Coach AI Terms of Service covering acceptable use, AI-generated content, subscriptions, liability, intellectual property, and termination.',
    canonical: `${SITE_URL}/terms`,
    url: `${SITE_URL}/terms`,
    image: OG_IMAGE,
  });

  return (
    <div className="min-h-screen bg-midnight">
      <JsonLd
        data={breadcrumbSchema([
          { name: 'Home', url: `${SITE_URL}/` },
          { name: 'Terms of Service', url: `${SITE_URL}/terms` },
        ])}
      />
      <Navbar />
      <main>
        {/* Header */}
        <section className="pt-32 pb-16">
          <div className="w-full px-4 md:px-8 lg:px-12">
            <div className="max-w-4xl mx-auto">
              <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: 'Terms of Service' }]} />
              <p className="text-champagne-gold text-sm font-medium tracking-wider uppercase mb-3">Legal</p>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-silver-grey mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                Terms of Service
              </h1>
              <p className="text-silver-grey/60 text-sm">
                Last updated: June 29, 2026
              </p>
            </div>
          </div>
        </section>

        {/* Divider */}
        <div className="max-w-4xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="border-t border-royal-navy/30" />
        </div>

        {/* Terms Content */}
        <section className="py-12 md:py-16">
          <div className="w-full px-4 md:px-8 lg:px-12">
            <div className="max-w-4xl mx-auto space-y-10">
              {sections.map((section, index) => (
                <div key={index}>
                  <h2 className="text-lg md:text-xl font-semibold text-champagne-gold mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {section.title}
                  </h2>
                  <p className="text-silver-grey/80 leading-relaxed text-sm md:text-base">
                    {section.content}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Back link */}
        <section className="pb-16">
          <div className="w-full px-4 md:px-8 lg:px-12">
            <div className="max-w-4xl mx-auto">
              <a
                href="/"
                className="inline-flex items-center gap-2 text-champagne-gold hover:text-champagne-gold/80 transition-colors duration-300 text-sm"
              >
                <i className="ri-arrow-left-line" />
                Back to Home
              </a>
            </div>
          </div>
        </section>
      </main>
      <FooterSection />
    </div>
  );
}